/**
 * <PackagesPlannerIntel /> — enhanced Package Planner with multiple views:
 *   1. Item × Designation hours matrix
 *   2. Workload by designation
 *   3. Category trend (last 12 months)
 *
 * Data source: /workspaces/{ws}/packages/planner-intel
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Layers, Users, TrendingUp, Grid3x3, UserRound } from "lucide-react";
import EmptyState from "@/components/EmptyState";

const STATUS_OPTIONS = [
  { value: "active", label: "Active only" },
  { value: "active,paused", label: "Active + Paused" },
  { value: "all", label: "All statuses" },
];

const CATEGORY_TONES = {
  social: "bg-pink-500",
  seo: "bg-emerald-500",
  ads: "bg-amber-500",
  content: "bg-sky-500",
  web: "bg-indigo-500",
  email: "bg-purple-500",
  meeting: "bg-teal-500",
  documentation: "bg-orange-500",
  adhoc: "bg-red-500",
  other: "bg-muted-foreground",
};

export default function PackagesPlannerIntel({ wsId }) {
  const auth = useAuth();
  const ws = wsId || auth.activeWorkspaceId;
  const [status, setStatus] = useState("active");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Feb 2026 · Fallback hint — when `status=active` returns 0 packages
  // but the workspace actually has packages in other statuses, probe
  // `status=all` and surface a dismissible banner suggesting the switch.
  // Keeps users from staring at an empty Planner thinking the module
  // is broken when their packages are simply in `draft` or `paused`.
  const [fallbackHint, setFallbackHint] = useState(null); // { total, byStatus }
  const [hintDismissed, setHintDismissed] = useState(false);

  useEffect(() => {
    if (!ws) return;
    let alive = true;
    setLoading(true);
    api.get(`/workspaces/${ws}/packages/planner-intel?status=${status}`)
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ws, status]);

  // Fallback probe — only when the current filter is 'active' AND
  // returned zero. Hits status=all once per empty state. Dismissed
  // state is per-workspace so switching away + back doesn't resurface
  // a hint the user already acknowledged.
  useEffect(() => {
    if (!ws || loading) return;
    if (status !== "active") { setFallbackHint(null); return; }
    if (!data || data.packages_count !== 0) { setFallbackHint(null); return; }
    let alive = true;
    api.get(`/workspaces/${ws}/packages/planner-intel?status=all`)
      .then((r) => {
        if (!alive) return;
        const n = r?.data?.packages_count || 0;
        if (n > 0) setFallbackHint({ total: n });
        else setFallbackHint(null);
      })
      .catch(() => { if (alive) setFallbackHint(null); });
    return () => { alive = false; };
  }, [ws, status, data, loading]);

  // Reset the dismissed flag whenever the workspace changes so moving
  // between workspaces doesn't permanently hide a legitimate hint.
  useEffect(() => { setHintDismissed(false); }, [ws]);

  if (loading) return <div className="text-sm text-muted-foreground py-6">Loading planner…</div>;

  // Feb 2026 · Status filter rendered ABOVE the empty-state so a
  // workspace with zero `active` packages (common for agencies that
  // keep retainers as `paused` between renewal windows, or that are
  // still building out drafts) can switch to "Active + Paused" or
  // "All statuses" from the same empty screen — previously the filter
  // was hidden inside the "data exists" branch, trapping users on a
  // screen that said "No packages match filter" with no way to fix it.
  const StatusFilter = (
    <div className="flex items-center gap-2 justify-end">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</span>
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className="h-9 w-48 rounded-none" data-testid="planner-status-filter">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  if (!data || data.packages_count === 0) {
    const showHint = fallbackHint && !hintDismissed && status === "active";
    return (
      <div className="space-y-4" data-testid="packages-planner-intel-empty">
        {StatusFilter}
        {showHint && (
          <div className="border border-amber-400/60 bg-amber-50 px-3 py-2 flex items-start gap-2"
               role="status" data-testid="planner-fallback-hint">
            <Layers className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 text-[12px]">
              <div className="font-semibold text-amber-900">
                Hidden by the Active-only filter
              </div>
              <div className="text-amber-800">
                This workspace has <b>{fallbackHint.total}</b> package{fallbackHint.total === 1 ? "" : "s"} in draft or paused status.
                Switch the filter to see them in the Planner.
              </div>
            </div>
            <button type="button"
                    onClick={() => setStatus("all")}
                    className="shrink-0 text-[10px] uppercase tracking-widest font-semibold text-amber-900 hover:underline border border-amber-500/60 hover:border-amber-700 px-2 py-1 bg-amber-100"
                    data-testid="planner-fallback-hint-apply">
              Show all statuses
            </button>
            <button type="button"
                    onClick={() => setHintDismissed(true)}
                    className="shrink-0 text-amber-700 hover:text-amber-900 px-1"
                    aria-label="Dismiss hint"
                    data-testid="planner-fallback-hint-dismiss">
              ×
            </button>
          </div>
        )}
        <EmptyState
          title="No packages match this status"
          description={
            status === "active"
              ? "You have no packages in 'active' status. Switch the status filter above to 'Active + Paused' or 'All statuses' to see draft and paused packages too."
              : "Change the status filter above or create a package line with effort hours to see aggregated data."
          }
          icon={Layers}
          testid="planner-empty"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="packages-planner-intel">
      <div className="flex items-center gap-2 justify-between">
        <div className="text-xs text-muted-foreground">
          Aggregating <span className="font-medium text-foreground">{data.packages_count}</span>{" "}
          packages across{" "}
          <span className="font-medium text-foreground">{(data.designations || []).length}</span>{" "}
          designations
        </div>
        {StatusFilter}
      </div>

      <Tabs defaultValue="grid" className="w-full">
        <TabsList className="rounded-none">
          <TabsTrigger value="grid" className="rounded-none" data-testid="planner-tab-grid">
            <Grid3x3 className="w-3.5 h-3.5 mr-1.5" /> Item × Designation
          </TabsTrigger>
          <TabsTrigger value="member" className="rounded-none" data-testid="planner-tab-member">
            <UserRound className="w-3.5 h-3.5 mr-1.5" /> Item × Team member
          </TabsTrigger>
          <TabsTrigger value="workload" className="rounded-none" data-testid="planner-tab-workload">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Workload
          </TabsTrigger>
          <TabsTrigger value="trend" className="rounded-none" data-testid="planner-tab-trend">
            <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Category trend
          </TabsTrigger>
        </TabsList>

        <TabsContent value="grid" className="pt-4">
          <ItemDesignationGrid data={data} />
        </TabsContent>
        <TabsContent value="member" className="pt-4">
          <ItemMemberGrid data={data} />
        </TabsContent>
        <TabsContent value="workload" className="pt-4">
          <WorkloadView data={data} />
        </TabsContent>
        <TabsContent value="trend" className="pt-4">
          <CategoryTrendView data={data} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ────────────────────────  Item × Designation grid  ────────────────────────

function ItemDesignationGrid({ data }) {
  const desigs = data.designations || [];
  const rows = data.item_by_designation || [];
  const itemTotals = useMemo(() => {
    const map = {};
    (data.item_totals || []).forEach((t) => { map[t.item] = t; });
    return map;
  }, [data.item_totals]);
  const maxCell = useMemo(() => {
    let m = 0;
    rows.forEach((r) => desigs.forEach((d) => {
      const h = r.hours_by_designation?.[d] || 0;
      if (h > m) m = h;
    }));
    return m;
  }, [rows, desigs]);

  if (rows.length === 0 || desigs.length === 0) {
    return <div className="text-sm text-muted-foreground py-4">No hours breakdown yet. Add skill breakdown to package lines to see this grid.</div>;
  }

  return (
    <div className="border border-border overflow-x-auto" data-testid="planner-grid">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-secondary/40">
            <th className="text-left px-3 py-2 sticky left-0 bg-secondary/40 min-w-[200px] font-medium">
              Item type
            </th>
            {desigs.map((d) => (
              <th key={d} className="px-3 py-2 text-right font-medium whitespace-nowrap">{d}</th>
            ))}
            <th className="px-3 py-2 text-right font-medium border-l border-border">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const total = itemTotals[r.item]?.hours || 0;
            return (
              <tr key={r.item} className="border-b border-border/40 hover:bg-secondary/30" data-testid={`planner-row-${r.item}`}>
                <td className="px-3 py-2 sticky left-0 bg-background font-medium">{r.item}</td>
                {desigs.map((d) => {
                  const h = r.hours_by_designation?.[d] || 0;
                  const intensity = maxCell > 0 ? h / maxCell : 0;
                  return (
                    <td key={d} className="px-3 py-2 text-right font-mono" style={h > 0 ? { backgroundColor: `rgba(59, 130, 246, ${0.05 + intensity * 0.35})` } : {}}>
                      {h > 0 ? h.toFixed(1) : "—"}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right font-mono font-bold border-l border-border">{total.toFixed(1)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────  Item × Team member grid  ────────────────────────

function ItemMemberGrid({ data }) {
  const members = data.members || [];
  const rows = data.item_by_member || [];
  const itemTotals = useMemo(() => {
    const map = {};
    (data.item_totals || []).forEach((t) => { map[t.item] = t; });
    return map;
  }, [data.item_totals]);
  const maxCell = useMemo(() => {
    let m = 0;
    rows.forEach((r) => members.forEach((mem) => {
      const h = r.hours_by_member?.[mem.key] || 0;
      if (h > m) m = h;
    }));
    return m;
  }, [rows, members]);

  if (rows.length === 0 || members.length === 0) {
    return <div className="text-sm text-muted-foreground py-4">No team-member data yet. Add designations to package lines AND to employees so we can map hours to people.</div>;
  }

  return (
    <div className="border border-border overflow-x-auto" data-testid="planner-member-grid">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-secondary/40">
            <th className="text-left px-3 py-2 sticky left-0 bg-secondary/40 min-w-[200px] font-medium">Item type</th>
            {members.map((m) => (
              <th key={m.key} className="px-2 py-2 text-right font-medium whitespace-nowrap" title={m.designation}>
                <div className="truncate max-w-[120px]">{m.name}</div>
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-normal truncate">{m.designation}</div>
              </th>
            ))}
            <th className="px-3 py-2 text-right font-medium border-l border-border">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const total = itemTotals[r.item]?.hours || 0;
            return (
              <tr key={r.item} className="border-b border-border/40 hover:bg-secondary/30" data-testid={`planner-member-row-${r.item}`}>
                <td className="px-3 py-2 sticky left-0 bg-background font-medium">{r.item}</td>
                {members.map((mem) => {
                  const h = r.hours_by_member?.[mem.key] || 0;
                  const intensity = maxCell > 0 ? h / maxCell : 0;
                  const isUnstaffed = mem.key.startsWith("designation:");
                  return (
                    <td
                      key={mem.key}
                      className={`px-2 py-2 text-right font-mono ${isUnstaffed ? "text-amber-700" : ""}`}
                      style={h > 0 ? { backgroundColor: `rgba(${isUnstaffed ? "245, 158, 11" : "59, 130, 246"}, ${0.05 + intensity * 0.35})` } : {}}
                    >
                      {h > 0 ? h.toFixed(1) : "—"}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right font-mono font-bold border-l border-border">{total.toFixed(1)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="p-2 border-t border-border/60 text-[10px] text-muted-foreground">
        Amber cells indicate work assigned to designations with no staffed employees yet.
      </div>
    </div>
  );
}

// ────────────────────────  Workload  ────────────────────────

function WorkloadView({ data }) {
  const workload = data.designation_workload || [];
  const totalHours = workload.reduce((s, w) => s + w.hours, 0);
  const maxHours = Math.max(1, ...workload.map((w) => w.hours));

  if (workload.length === 0) {
    return <div className="text-sm text-muted-foreground py-4">No workload yet.</div>;
  }
  return (
    <div className="border border-border p-6 space-y-3" data-testid="planner-workload">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">Monthly workload by designation (active packages)</div>
        <div className="text-xs text-muted-foreground">{totalHours.toFixed(0)} planned hrs / month</div>
      </div>
      <div className="space-y-1.5">
        {workload.map((w) => (
          <div key={w.designation} className="flex items-center gap-3" data-testid={`workload-${w.designation}`}>
            <span className="text-xs w-36 truncate">{w.designation}</span>
            <div className="flex-1 h-2 bg-secondary">
              <div className="h-full bg-primary" style={{ width: `${(w.hours * 100) / maxHours}%` }} />
            </div>
            <span className="text-xs font-mono w-16 text-right">{w.hours.toFixed(1)} hrs</span>
            <span className="text-[10px] text-muted-foreground w-12 text-right">
              {totalHours > 0 ? `${Math.round((w.hours * 100) / totalHours)}%` : "0%"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────  Category trend  ────────────────────────

function CategoryTrendView({ data }) {
  const trend = data.category_trend || [];
  const allCats = useMemo(() => {
    const s = new Set();
    trend.forEach((m) => Object.keys(m.counts || {}).forEach((k) => s.add(k)));
    return Array.from(s).sort();
  }, [trend]);
  const maxMonth = useMemo(() => Math.max(1, ...trend.map((m) => Object.values(m.counts || {}).reduce((s, v) => s + v, 0))), [trend]);

  if (trend.length === 0 || allCats.length === 0) {
    return <div className="text-sm text-muted-foreground py-4">No trend data yet.</div>;
  }
  return (
    <div className="border border-border p-6 space-y-4" data-testid="planner-trend">
      <div className="label-eyebrow">Packages started per month — by category (last 12 months)</div>
      <div className="flex items-end gap-1 h-40">
        {trend.map((m) => {
          const total = Object.values(m.counts || {}).reduce((s, v) => s + v, 0);
          const heightPct = maxMonth > 0 ? (total * 100) / maxMonth : 0;
          return (
            <div key={m.month} className="flex-1 flex flex-col justify-end items-center group relative">
              <div className="flex flex-col w-full transition" style={{ height: `${heightPct}%`, minHeight: total ? "6px" : "2px" }}>
                {allCats.map((c) => {
                  const cnt = m.counts?.[c] || 0;
                  if (!cnt) return null;
                  const pct = total > 0 ? (cnt * 100) / total : 0;
                  return (
                    <div key={c} className={CATEGORY_TONES[c] || CATEGORY_TONES.other} style={{ height: `${pct}%` }} title={`${c}: ${cnt}`} />
                  );
                })}
              </div>
              <div className="absolute -bottom-6 text-[9px] text-muted-foreground font-mono">
                {m.month.slice(5)}
              </div>
              {total > 0 && (
                <div className="absolute -top-4 text-[9px] text-muted-foreground font-mono">
                  {total}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="pt-4 flex items-center gap-3 flex-wrap text-[11px] text-muted-foreground border-t border-border/40">
        {allCats.map((c) => (
          <span key={c} className="inline-flex items-center gap-1">
            <span className={`w-2.5 h-2.5 ${CATEGORY_TONES[c] || CATEGORY_TONES.other}`} />
            <span className="capitalize">{c}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
